import { useMemo } from 'react'
import { Maybe } from '@graphql-tools/utils'
import { Asset, createAsset, SmpteTimestamp, UpNextConfig } from '@/organisms/AngelPlayer'
import { EngagementDetail } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { GetEpisodeResponse } from '@/services/ProjectsService'
import { GetEpisodeByGuidQuery } from '@/types/codegen-federation'
import { buildLinkToEpisode } from '@/utils/EpisodeUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { isSeries } from '@/utils/project-utils/project-utils'

export interface UseEpisodeAssetConfig {
  momentsEnabled?: boolean
  defaultEngagementDetail?: Maybe<EngagementDetail>
}

export const usePureEpisodeAsset = (data?: GetEpisodeByGuidQuery): { asset?: Asset } => {
  const { query } = useSafeRouter()

  return useMemo(() => {
    const episode = data?.episode
    if (!episode) return { asset: undefined }

    const smpte = query.smpte as SmpteTimestamp
    const startTime = query?.position ? parseInt(query.position as string) : 0
    const position = smpte ?? startTime ?? 0

    const asset = createAsset({
      guid: episode.guid as string,
      projectSlug: episode.projectSlug as string,
      slug: episode.slug as string,
      name: episode?.name as string,
      url: episode.source?.url as string,
      title: episode.subtitle as string,
      subtitle: episode.subtitle as string,
      description: episode.description as string,
      position,
      episodeNumber: episode.episodeNumber as number,
      releaseDate: episode.releaseDate,
      seasonId: episode.seasonId as string,
      duration: episode.source?.duration as number,
      videoType: 'episode',
      credits: episode.source?.credits as number,
    })

    return {
      asset,
    }
  }, [data?.episode, query])
}

const getPositionBasedOnWatchPositionAndCredits = (episode: GetEpisodeResponse['episode']) => {
  // if the user hasn't watched before, start at the beginning
  if (!episode?.watchPosition?.position) return 0

  // if the content doesn't have a credits marker, throw up our hands and start where they left off
  if (!episode?.source?.credits) return episode?.watchPosition?.position

  const lastWatchPositionWasBeyondCredits = episode?.watchPosition?.position > episode?.source?.credits
  if (lastWatchPositionWasBeyondCredits) {
    // the user is probably rewatching, lets start at the beginning
    return 0
  } else {
    return episode?.watchPosition?.position
  }
}

export const useEpisodeAsset = (
  episodeResponse?: GetEpisodeResponse,
  config?: UseEpisodeAssetConfig,
): { asset?: Asset } => {
  const { push, query } = useSafeRouter()
  const { episode, project } = episodeResponse || {}

  const asset = useMemo(() => {
    if (!episode || !project) return

    let upNextMetadata: UpNextConfig | undefined

    if (episode.upNext?.guid && project.slug && isSeries(project)) {
      upNextMetadata = {
        goToNext: () => {
          push(buildLinkToEpisode(episode.upNext, project.slug))
        },
        showTime: Math.min(
          episode.source.credits ? episode.source.credits + 8 : Number.POSITIVE_INFINITY,
          episode.source.duration - 15,
        ),
        autoGoToNextAfterMs: 10000,
      }
    }

    const smpte = query.smpte as SmpteTimestamp
    const startTime = query.position ? parseInt(query.position as string) : undefined
    const position =
      smpte ??
      startTime ??
      config?.defaultEngagementDetail?.startTime ??
      getPositionBasedOnWatchPositionAndCredits(episode)

    return createAsset({
      guid: episode.guid,
      publisherName: project.publisherName,
      projectSlug: project.slug,
      projectName: project.name,
      slug: episode.slug,
      name: episode?.name,
      url: episode.source?.url,
      title: episode.subtitle,
      subtitle: episode.subtitle,
      description: episode.description,
      position,
      upNext: upNextMetadata,
      episodeNumber: episode.episodeNumber,
      releaseDate: episode.releaseDate,
      seasonId: episode.seasonId,
      duration: episode.source?.duration,
      videoType: 'episode',
      credits: episode.source?.credits,
      vmapUrl: episode.vmapUrl,
    })
  }, [episode, project, config?.defaultEngagementDetail?.startTime, push, query.position, query.smpte])

  return { asset }
}
